import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["selected", "onClick"]
const _hoisted_2 = { class: "thumbnail" }
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = { class: "name" }
const _hoisted_5 = ["accept"]
const _hoisted_6 = { class: "lightbox-card" }
const _hoisted_7 = { class: "lightbox-content" }
const _hoisted_8 = { class: "lightbox-bottom" }

import { VModal, VButton, VIconSvg } from "@/components";
import { Cropper } from "vue-advanced-cropper";
import { ref, computed, watch } from "vue";
import MediaAssetApi from "@/api/media-asset";
import UserMediaAssetAdminApi from "@/api/admin/users/media-asset";
import { generateVideoThumbnailViaUrl } from "@rajesh896/video-thumbnails-generator";
import QueryBuilder from "@/services/query-builder";
import NotifyService from "@/services/notify.service";
import i18n from "@/plugins/vue-i18n";
import { head, isEmpty } from "lodash";

type ImageType = "jpg" | "jpeg" | "png";
type VideoType = "mp4" | "webm" | "m4a";

interface Props {
  mediaAsset?: {
    id: number | null;
    originName: string;
    url: string;
    width: number;
    height: number;
  };
  mediaType: "image" | "video";
  maxWeight?: number;
  maxDuration?: number;
  allowedFileTypes?: Array<ImageType | VideoType>;
  width: number;
  height: number;
  sizeCheckRule: "equal" | "greaterThanOrEqual";

  side?: "advert" | "admin";
  userId?: number;
}


const __default__ = {
  compatConfig: {
    MODE: 3,
  },
};

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'VModalMediaAssets',
  props: {
    mediaAsset: { default: () => ({
    id: null,
    originName: "",
    url: "",
    width: 0,
    height: 0,
  }) },
    mediaType: { default: "image" },
    maxWeight: { default: 5 },
    maxDuration: { default: 30 },
    allowedFileTypes: { default: () => ["jpg", "jpeg", "png"] },
    width: { default: 0 },
    height: { default: 0 },
    sizeCheckRule: { default: "equal" },
    side: { default: "advert" },
    userId: { default: undefined }
  },
  emits: ["update:mediaAsset"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;

const isShownModal = ref(false);
const mediaAssetValue = ref(undefined);
const mediaAssetsData = ref([]);
const files = ref([]);
const isShownLightboxState = ref(false);
const cropper = ref([]);
const fileInput = ref();

const mediaAssets = computed(() =>
  mediaAssetsData.value.map((mediaAsset) => ({
    ...mediaAsset,
    isSelected: mediaAsset.id === mediaAssetValue.value?.id,
  }))
);

const show = async () => {
  mediaAssetValue.value = props.mediaAsset;
  await getMediaAssets();
  isShownModal.value = true;
};

const apply = () => {
  emit("update:mediaAsset", mediaAssetValue.value);

  close();
};

const close = () => {
  mediaAssetValue.value = undefined;
  mediaAssetsData.value = [];
  isShownModal.value = false;
};

const getMediaAssets = async () => {
  const qb = new QueryBuilder();

  qb.where("mediaType", "=", props.mediaType);
  qb.orderBy("id", "desc");

  const operators = {
    equal: "=",
    greaterThanOrEqual: ">=",
  };

  switch (props.mediaType) {
    case "image":
      qb.where("width", operators[props.sizeCheckRule], props.width);
      qb.where("height", operators[props.sizeCheckRule], props.height);
      break;
    case "video":
      break;
  }

  const arrayQuery = qb.query;

  const result = await mediaAssetAllApi({ arrayQuery });

  const selectedMediaAsset = result.find(({ id }) => id === mediaAssetValue.value?.id);
  const availableMediaAssets = result.filter(({ id }) => id !== mediaAssetValue.value?.id);

  mediaAssetsData.value = selectedMediaAsset
    ? [selectedMediaAsset, ...availableMediaAssets]
    : availableMediaAssets;

  mediaAssetsData.value.map(async (item) => {
    switch (item.mediaType) {
      case "image":
        item.thumbnail = item.url;
        break;
      case "video":
        if (!item.thumbnail) item.thumbnail = await generateVideoThumbnailViaUrl(item.url, 5);
        break;
    }

    return item;
  });
};

const fileTypesString = computed(() => {
  return props.allowedFileTypes.map((item) => `.${item}`).join(", ");
});

const isShownLightBox = computed(() => {
  return (
    !!files.value.find(({ instance }) => instance.type.startsWith("image/")) &&
    isShownLightboxState.value
  );
});

const aspectRatio = computed(() => {
  if (props.height === 0) return 0;

  return props.width / props.height;
});

const onChangeFileInput = async (event: Event) => {
  const data = [];

  for (const file of event.target.files) {
    let processedFile = file;

    if (file && file.type.startsWith("image/")) {
      processedFile = await resizeImage(file);
      isShownLightboxState.value = true;
    }

    data.push({
      instance: processedFile,
    });
  }

  files.value = data;
  fileInput.value.value = null;
};

const resizeImage = (file: File): Promise<File> => {
  const minWidth = props.width;
  const minHeight = props.height;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (readerEvent) => {
      const img = new Image();

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Scale only if width OR height is smaller
        const widthScale = width < minWidth ? minWidth / width : 1;
        const heightScale = height < minHeight ? minHeight / height : 1;

        const scale = Math.max(widthScale, heightScale);

        // If already satisfies one dimension, don't upscale
        if (scale === 1) {
          return resolve(file);
        }

        width = Math.round(width * scale);
        height = Math.round(height * scale);

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Canvas is empty");

            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve(resizedFile);
          },
          file.type,
          1
        );
      };

      img.onerror = reject;
      img.src = readerEvent.target?.result as string;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const upload = async (file: File) => {
  const fileName = file.name;

  switch (props.mediaType) {
    case "image":
      if (await isValidImage(file)) {
        await mediaAssetUploadImageApi(file);

        NotifyService.notify({
          type: "success",
          text: i18n.t("component.notify.success.imageUploadedSuccessfully", { fileName }),
        });
      }

      break;
    case "video":
      if (await isValidVideo(file)) {
        await mediaAssetUploadVideoApi(file);

        NotifyService.notify({
          type: "success",
          text: i18n.t("component.notify.success.videoUploadedSuccessfully", { fileName }),
        });
      }

      break;
  }
};

const isValidImage = async (file: File) => {
  const fileName = file.name;

  const fileCheck = new Promise((resolve) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const fileWeight = (event.total / (1024 * 1024)).toFixed(2);
      let isValid = props.maxWeight >= fileWeight;

      if (!isValid) {
        NotifyService.notify({
          type: "error",
          text: i18n.t("component.notify.error.wrongImageWeight", {
            weight: props.maxWeight,
            fileName,
          }),
          duration: NotifyService.DURATION_LONG,
        });
      }

      const newImage = new Image();

      newImage.src = event.target.result;

      newImage.onload = () => {
        const imageWidth = newImage.width;
        const imageHeight = newImage.height;

        switch (props.sizeCheckRule) {
          case "equal":
            isValid = imageWidth === props.width && imageHeight === props.height;
            break;
          case "greaterThanOrEqual":
            isValid = imageWidth >= props.width && imageHeight >= props.height;
            break;
        }

        if (!isValid) {
          NotifyService.notify({
            type: "error",
            text: i18n.t("component.notify.error.wrongImageSize", { fileName }),
            duration: NotifyService.DURATION_LONG,
          });
        }

        resolve(isValid);
      };
    };
  });

  return await Promise.resolve(fileCheck);
};

const isValidVideo = async (file: File) => {
  const fileName = file.name;

  const fileCheck = new Promise((resolve) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const fileWeight = (event.total / (1024 * 1024)).toFixed(2);
      let isValid = +props.maxWeight >= +fileWeight;

      if (!isValid) {
        NotifyService.notify({
          type: "error",
          text: i18n.t("component.notify.error.wrongVideoWeight", {
            weight: props.maxWeight,
            fileName,
          }),
          duration: NotifyService.DURATION_LONG,
        });
      }

      const videoElement = document.createElement("video");

      videoElement.preload = "metadata";

      videoElement.onloadedmetadata = () => {
        window.URL.revokeObjectURL(videoElement.src);
        const duration = videoElement.duration;

        isValid = duration <= props.maxDuration;

        if (!isValid) {
          NotifyService.notify({
            type: "error",
            text: i18n.t("component.notify.error.wrongVideoDuration", { fileName }),
            duration: NotifyService.DURATION_LONG,
          });
        }

        resolve(isValid);
      };

      videoElement.src = URL.createObjectURL(file);
    };
  });

  return await Promise.resolve(fileCheck);
};

const mediaAssetAllApi = async (query) => {
  let result;

  switch (props.side) {
    case "admin":
      result = await UserMediaAssetAdminApi.all(props.userId, query);

      break;
    default:
      result = await MediaAssetApi.all(query);

      break;
  }

  return result;
};

const mediaAssetUploadImageApi = async (file) => {
  let result;

  const formData = new FormData();

  formData.append("media_assets[]", file);
  formData.append("image_video", "0");

  switch (props.side) {
    case "admin":
      result = await UserMediaAssetAdminApi.uploadImage(props.userId, formData);

      break;
    default:
      result = await MediaAssetApi.uploadImage(formData);

      break;
  }

  return result;
};

const mediaAssetUploadVideoApi = async (file) => {
  let result;

  const formData = new FormData();

  formData.append("media_assets[]", file);
  formData.append("image_video", "1");

  switch (props.side) {
    case "admin":
      result = await UserMediaAssetAdminApi.uploadVideo(props.userId, formData);

      break;
    default:
      result = await MediaAssetApi.uploadVideo(formData);

      break;
  }

  return result;
};

const onClickCancelLightbox = () => {
  isShownLightboxState.value = false;
  files.value = [];
};

const onClickApplyLightbox = async () => {
  for (const [index, item] of cropper.value.entries()) {
    const originalFile = files.value[index].instance;
    const { canvas } = item.getResult();

    if (canvas) {
      const file = await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          const result = new File([blob], originalFile.name, { type: originalFile.type });

          resolve(result);
        }, originalFile.type);
      });

      if (file) await upload(file);
    }
  }

  await getMediaAssets();
  applyLatestWhenNotSelected();

  isShownLightboxState.value = false;
  files.value = [];
};

const applyLatestWhenNotSelected = () => {
  if (isEmpty(props.mediaAsset)) {
    const latest = head(mediaAssetsData.value);

    if (latest) emit("update:mediaAsset", latest);
  }
};

watch(
  () => props.mediaAsset,
  () => (mediaAssetValue.value = props.mediaAsset),
  { deep: true, immediate: true }
);

watch(
  () => files.value,
  async () => {
    for (const file of files.value) {
      if (file.instance && file.instance.type.startsWith("image/")) {
        const reader = new FileReader();

        reader.onload = () => {
          file.dataUrl = reader.result;
        };

        reader.readAsDataURL(file.instance);
      } else {
        for (const file of files.value) {
          await upload(file.instance);
        }

        await getMediaAssets();
        applyLatestWhenNotSelected();
      }
    }
  }
);

__expose({
  close,
  show,
});

return (_ctx: any,_cache: any) => {
  const _component_perfect_scrollbar = _resolveComponent("perfect-scrollbar")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createBlock(_unref(VModal), {
    modelValue: isShownModal.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isShownModal).value = $event)),
    "max-width": "lg",
    title: _ctx.$t('label.mediaAssets'),
    class: "modal-media-assets",
    onClose: close
  }, {
    "footer-left": _withCtx(() => [
      _createElementVNode("input", {
        id: "media-asset-input",
        ref_key: "fileInput",
        ref: fileInput,
        multiple: "",
        hidden: "",
        type: "file",
        accept: fileTypesString.value,
        onChange: onChangeFileInput
      }, null, 40, _hoisted_5),
      _createVNode(_unref(VButton), {
        text: _ctx.$t('button.upload'),
        variant: "primary",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$refs.fileInput.click()))
      }, null, 8, ["text"])
    ]),
    "footer-right": _withCtx(() => [
      _createVNode(_unref(VButton), {
        text: _ctx.$t('button.apply'),
        variant: "secondary",
        onClick: apply
      }, null, 8, ["text"])
    ]),
    default: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mediaAssets.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          selected: item.isSelected,
          class: "media-asset",
          onClick: ($event: any) => (mediaAssetValue.value = item)
        }, [
          _createElementVNode("div", _hoisted_2, [
            (item?.thumbnail)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: item.thumbnail,
                  alt: item.originName
                }, null, 8, _hoisted_3))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (props.mediaType === 'image')
                    ? (_openBlock(), _createBlock(_unref(VIconSvg), {
                        key: 0,
                        name: "Image2",
                        size: "3xl",
                        color: "fc-subduet"
                      }))
                    : _createCommentVNode("", true),
                  (props.mediaType === 'video')
                    ? (_openBlock(), _createBlock(_unref(VIconSvg), {
                        key: 1,
                        name: "Video",
                        size: "3xl",
                        color: "fc-subduet"
                      }))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_4, _toDisplayString(item.originName), 1)
                ], 64))
          ])
        ], 8, _hoisted_1))
      }), 128)),
      (isShownLightBox.value)
        ? (_openBlock(), _createBlock(_component_perfect_scrollbar, {
            key: 0,
            class: "lightbox"
          }, {
            default: _withCtx(() => [
              _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file, index) => {
                    return (_openBlock(), _createBlock(_unref(Cropper), {
                      key: index,
                      ref_for: true,
                      ref_key: "cropper",
                      ref: cropper,
                      src: file.dataUrl,
                      canvas: { height: _ctx.height, width: _ctx.width },
                      "stencil-props": {
              resizable: true,
              aspectRatio: aspectRatio.value,
            },
                      "resize-image": { wheel: false },
                      "default-size": { height: _ctx.height, width: _ctx.width },
                      class: "cropper"
                    }, null, 8, ["src", "canvas", "stencil-props", "default-size"]))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_unref(VButton), {
                    text: _ctx.$t('button.cancel'),
                    variant: "secondary",
                    onClick: onClickCancelLightbox
                  }, null, 8, ["text"]),
                  _createVNode(_unref(VButton), {
                    text: _ctx.$t('button.apply'),
                    onClick: onClickApplyLightbox
                  }, null, 8, ["text"])
                ])
              ])), [
                [_directive_click_outside, onClickCancelLightbox]
              ])
            ], undefined, true),
            _: 1
          }))
        : _createCommentVNode("", true)
    ], undefined, true),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})